import axios from 'axios'
import { useQuery } from 'react-query'
import type {
  KpiName,
  KpiResponse,
  EvolutionResponse,
  BarResponse,
  HistogramResponse,
  PieResponse,
  UuidsResponse,
  JoulyDailyFilter,
  Range,
} from '../types/joulyDailyTypes'

// ─── Base URL ─────────────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL + '/api/v1'
const BASE = `${BACKEND_URL}/analytics/jouly/daily`

// ─── Range serialization ──────────────────────────────────────────────────────

// Send the range as a single Flux duration (e.g. `-30d`) which `ExtractQueryObjectMultiParams`
// stores in `q.Range` (no comma → no start/stop split). The Go validator `isValidFluxRange`
// accepts durations and RFC3339 only; `now()` is rejected. The backend's
// `joulyDailyRangeFlux` then emits `|> range(start: -30d)` (Flux defaults stop to now()).
const rangeToFlux: Record<Range, string> = {
  '7d':  '-7d',
  '30d': '-30d',
  '90d': '-90d',
}

// ─── Param builder ────────────────────────────────────────────────────────────

function buildParams(filter: JoulyDailyFilter): URLSearchParams {
  return new URLSearchParams({
    range: rangeToFlux[filter.range],
    uuids: filter.uuids.join(','),
  })
}

// ─── Fetcher functions ────────────────────────────────────────────────────────

export async function fetchJoulyDailyUuids(): Promise<UuidsResponse> {
  return (await axios.get<UuidsResponse>(`${BASE}/uuids`)).data
}

export async function fetchJoulyDailyKpi(
  name: KpiName,
  filter: JoulyDailyFilter
): Promise<KpiResponse> {
  const params = buildParams(filter)
  return (
    await axios.get<KpiResponse>(`${BASE}/kpi/${encodeURIComponent(name)}?${params.toString()}`)
  ).data
}

export async function fetchJoulyDailyEvolution(
  filter: JoulyDailyFilter
): Promise<EvolutionResponse> {
  const params = buildParams(filter)
  return (
    await axios.get<EvolutionResponse>(`${BASE}/line/evolution?${params.toString()}`)
  ).data
}

export async function fetchJoulyDailyBar(
  filter: JoulyDailyFilter
): Promise<BarResponse> {
  const params = buildParams(filter)
  return (
    await axios.get<BarResponse>(`${BASE}/bar/by-uuid?${params.toString()}`)
  ).data
}

export async function fetchJoulyDailyHistogram(
  filter: JoulyDailyFilter
): Promise<HistogramResponse> {
  const params = buildParams(filter)
  return (
    await axios.get<HistogramResponse>(`${BASE}/histogram?${params.toString()}`)
  ).data
}

export async function fetchJoulyDailyPie(
  filter: JoulyDailyFilter
): Promise<PieResponse> {
  const params = buildParams(filter)
  return (
    await axios.get<PieResponse>(`${BASE}/pie/classes?${params.toString()}`)
  ).data
}

// ─── React Query hooks ────────────────────────────────────────────────────────

// Stable, primitive-only key fragment derived from the filter — sorting uuids
// makes the key invariant under selection order and the join collapses the array
// to a single string so query-key reference equality works across renders.
function filterKey(filter: JoulyDailyFilter): readonly [Range, string] {
  return [filter.range, [...filter.uuids].sort().join(',')] as const
}

export function useJoulyDailyUuids() {
  return useQuery<UuidsResponse>({
    queryKey: ['jouly-daily', 'uuids'],
    queryFn: fetchJoulyDailyUuids,
  })
}

export function useJoulyDailyKpi(name: KpiName, filter: JoulyDailyFilter) {
  return useQuery<KpiResponse>({
    queryKey: ['jouly-daily', 'kpi', name, ...filterKey(filter)],
    queryFn: () => fetchJoulyDailyKpi(name, filter),
    enabled: filter.uuids.length > 0,
  })
}

export function useJoulyDailyEvolution(filter: JoulyDailyFilter) {
  return useQuery<EvolutionResponse>({
    queryKey: ['jouly-daily', 'evolution', ...filterKey(filter)],
    queryFn: () => fetchJoulyDailyEvolution(filter),
    enabled: filter.uuids.length > 0,
  })
}

export function useJoulyDailyBar(filter: JoulyDailyFilter) {
  return useQuery<BarResponse>({
    queryKey: ['jouly-daily', 'bar', ...filterKey(filter)],
    queryFn: () => fetchJoulyDailyBar(filter),
    enabled: filter.uuids.length > 0,
  })
}

export function useJoulyDailyHistogram(filter: JoulyDailyFilter) {
  return useQuery<HistogramResponse>({
    queryKey: ['jouly-daily', 'histogram', ...filterKey(filter)],
    queryFn: () => fetchJoulyDailyHistogram(filter),
    enabled: filter.uuids.length > 0,
  })
}

export function useJoulyDailyPie(filter: JoulyDailyFilter) {
  return useQuery<PieResponse>({
    queryKey: ['jouly-daily', 'pie', ...filterKey(filter)],
    queryFn: () => fetchJoulyDailyPie(filter),
    enabled: filter.uuids.length > 0,
  })
}
